import { CSSResult, html, LitElement, nothing, PropertyValues, TemplateResult, unsafeCSS } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { consume, ContextConsumer } from '@lit/context';
import { repeat } from 'lit/directives/repeat.js';
import { createRef, Ref, ref } from 'lit/directives/ref.js';
import { ProductFamilyProduct } from './product-family.js';
import { i18nContext } from '../../../scripts/context/i18nContext.js';
import { Messages } from '../../../@types/wishlist.js';
import { productFamilyContext, ProductFamilyState } from '../../../scripts/context/productFamilyContext.js';
import styles from './product-family-slider.scss?inline';
import { sharedCss } from '../../../scripts/style-bundle/shared.js';
import grids from '../../../styles/grids.scss?inline';
import '../product-family-product/product-family-product.js';
import '../wishlist-item-slider/wishlist-item-slider.js';
import RecoTracking from '../tracking/recoTracking.js';
import { config } from '../../../scripts/service/config.js';
import { getBackButton } from '../base-layer/base-layer.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import baseLayerStyles from '../base-layer/base-layer.scss?inline';

@customElement('ents-product-family-slider')
export class ProductFamilyComponent extends LitElement {
  @consume({ context: productFamilyContext, subscribe: true })
  @property({ attribute: false })
  private productFamilyState!: ProductFamilyState;

  @consume({ context: i18nContext })
  private messages!: Messages;

  private recoTracking: RecoTracking | null = null;

  private productFamilyGridRef: Ref<HTMLElement> = createRef();

  // Reset recoTracking whenever the product-family context changes.
  // The consumer registers with `this` on construction; the field reference keeps it alive.
  private contextConsumer = new ContextConsumer(this, {
    context: productFamilyContext,
    subscribe: true,
    callback: () => {
      if (this.recoTracking !== null) {
        this.recoTracking.unobserve();
        this.recoTracking = null;
      }
    },
  });

  static get styles(): CSSResult[] {
    return [sharedCss, unsafeCSS(styles), unsafeCSS(grids), unsafeCSS(baseLayerStyles)];
  }

  protected updated(_changedProperties: PropertyValues) {
    super.updated(_changedProperties);

    // Don't init multiple times
    if (this.recoTracking !== null) {
      return;
    }

    if (!this.productFamilyGridRef.value) {
      return;
    }

    // only init if there are matches available
    if (this.productFamilyState.sliderData?.matches?.length) {
      const rulePrefix = this.getRulePrefix();
      const ruleSuffix = this.productFamilyState.beauty ? 'beauty' : 'fashion';
      const rule = [rulePrefix, ruleSuffix].join('.');

      this.recoTracking = new RecoTracking(this.productFamilyGridRef.value, 'ents-product-family-product', {
        currency: {
          code: config.currency ?? '',
        },
        promotion_data: {
          type: 'list',
          name: 'product_family',
          placement: 'drawer',
          rule,
        },
        event_data: {
          is_interaction_event: false,
        },
      });

      this.recoTracking.observe();
    }
  }

  protected render(): TemplateResult {
    const sliderData = this.productFamilyState.sliderData;

    const backButtonHtml = html`
      ${this.productFamilyState.backButtonCallback === undefined
        ? nothing
        : getBackButton(this.messages, 'ents-product-family-layer', this.backButtonAction.bind(this))}
    `;

    if (sliderData?.matches === undefined) {
      return html`
        <ents-base-layer
          drawer-test-id="ents-product-family-layer"
          ?showLayer="${this.productFamilyState.open}"
          @ents-layer-closed="${this.setLayerClosed}"
        >
          ${backButtonHtml} ${this.getError()}
        </ents-base-layer>
      `;
    }

    return html`
      <ents-base-layer
        drawer-test-id="ents-product-family-layer"
        ?showLayer="${this.productFamilyState.open}"
        @ents-layer-closed="${this.setLayerClosed}"
      >
        ${backButtonHtml}
        <h1 class="ents-product-family-headline">
          <span class="ents-heading-medium" data-test-ents-product-family-headline>
            ${unsafeHTML(this.getHeadline())}
          </span>
          <span class="ents-copy-small" data-test-ents-product-family-headline-count>
            (${sliderData.matches.length})</span
          >
        </h1>
        <div class="ents-product-family-slider-grid" ${ref(this.productFamilyGridRef)}>
          ${repeat(sliderData?.matches, (productFamilyProduct, index) =>
            this.renderProductFamilyProduct(productFamilyProduct, index),
          )}
        </div>
      </ents-base-layer>
    `;
  }

  private getHeadline(): string | undefined {
    switch (this.productFamilyState.displayType) {
      case 'mixandmatch':
        return this.messages['ents_wishlist_drawer_mixandmatch_headline'];
      case 'productline':
        return this.messages['ents_wishlist_drawer_productline_headline'];
      case 'capsule':
        return this.messages['ents_wishlist_drawer_capsule_headline'];
      default:
        return '';
    }
  }

  private getRulePrefix(): string | undefined {
    switch (this.productFamilyState.displayType) {
      case 'mixandmatch':
        return 'mix_match';
      case 'productline':
        return 'linie';
      case 'capsule':
        return 'capsule';
      default:
        return '';
    }
  }

  private getError(): string | undefined {
    switch (this.productFamilyState.displayType) {
      case 'mixandmatch':
        return this.messages['ents_wishlist_empty_mixandmatch'];
      case 'productline':
        return this.messages['ents_wishlist_empty_productline'];
      case 'capsule':
        return this.messages['ents_wishlist_empty_capsule'];
      default:
        return '';
    }
  }

  private renderProductFamilyProduct(product: ProductFamilyProduct, index: number): TemplateResult {
    return html`
      <article>
        <ents-product-family-product
          class="ents-product-family-product"
          .product="${product}"
          data-position="${index}"
          data-tracking-data="${JSON.stringify(product.trackingData)}"
          .position="${index + 1}"
        >
        </ents-product-family-product>
      </article>
    `;
  }

  private backButtonAction() {
    if (this.productFamilyState.backButtonCallback) {
      this.productFamilyState.backButtonCallback();
    }
    this.setLayerClosed();
    this.dispatchEvent(new Event('ents-layer-back', { composed: true, bubbles: true }));
  }

  private setLayerClosed = () => {
    if (this.productFamilyState.open) {
      this.productFamilyState.update({ open: false, backButtonCallback: undefined });
    }
  };
}
